import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Settings as SettingsIcon, Bell, Save, FileText, Trash2, Sparkles } from "lucide-react";
import { API } from "../lib/apiClient";
import { BackupCcEditor } from "./BackupCcEditor";
import { EmailLogsPanel } from "./EmailLogsPanel";
import { LogoUploader } from "./LogoUploader";
import { SignatureEditor } from "./SignatureEditor";

export function CronStatusPanel({ defaultEditing = false }) {
  const [status, setStatus] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [testingReminder, setTestingReminder] = useState(false);
  const [reminderMsg, setReminderMsg] = useState("");
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState("");
  const [editingPrefs, setEditingPrefs] = useState(defaultEditing);
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/cron/status`),
        axios.get(`${API}/user/preferences`),
      ]);
      setStatus(s.data);
      setPrefs(p.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Durum alınamadı");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const sendReminderNow = async () => {
    setTestingReminder(true);
    setReminderMsg("");
    try {
      const { data } = await axios.post(`${API}/reminders/renewals/send-now`);
      if (data.sent) setReminderMsg(`Hatırlatma gönderildi · ${data.total} poliçe`);
      else setReminderMsg(data.reason === "no-renewals" ? "Önümüzdeki günlerde yenilenecek poliçe yok" : (data.reason === "already-sent-today" ? "Bugün zaten gönderilmiş" : "Gönderilmedi"));
      await fetchAll();
    } catch (e) {
      setReminderMsg(`Gönderim başarısız: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setTestingReminder(false);
    }
  };
  const savePrefs = async (patch) => {
    setSavingPrefs(true);
    setPrefsMsg("");
    try {
      const { data } = await axios.put(`${API}/user/preferences`, patch);
      setPrefs(data);
      setPrefsMsg("Tercihler kaydedildi");
      await fetchAll();
      setTimeout(() => setPrefsMsg(""), 2500);
    } catch (e) {
      setPrefsMsg(`Kaydedilemedi: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSavingPrefs(false);
    }
  };
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };
  const daysAgo = (iso) => {
    if (!iso) return null;
    try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return null; }
  };
  const toneFor = (iso, maxDays) => {
    const d = daysAgo(iso);
    if (d === null) return "warning";
    return d > maxDays ? "danger" : "ok";
  };
  const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
  const utcToTr = (h) => (h + 3) % 24;  // Türkiye = UTC+3
  if (loading) return <section className="panel" data-testid="cron-status-loading" style={{ marginBottom: 16 }}><p className="muted" style={{ margin: 0 }}>Otomatik hatırlatmalar yükleniyor…</p></section>;
  if (error) return <section className="panel" data-testid="cron-status-error" style={{ marginBottom: 16 }}><p style={{ margin: 0, color: "#dc2626" }}>Otomatik hatırlatma durumu alınamadı: {error}</p></section>;
  if (!status || !prefs) return null;
  const w = status.weeklyBackup || {};
  const r = status.dailyRenewal || {};
  const m = status.monthlySummary || {};
  const t = status.trashPurge || {};
  const AVAILABLE_BUCKETS = [1, 3, 7, 15, 30, 45, 60];
  const toggleBucket = (b) => {
    const cur = prefs.reminderBuckets || [];
    const next = cur.includes(b) ? cur.filter(x => x !== b) : [...cur, b].sort((a, b) => a - b);
    if (next.length === 0) return;
    savePrefs({ reminderBuckets: next });
  };
  const sendMonthlyNow = async () => {
    setTestingReminder(true);
    setReminderMsg("");
    try {
      const { data } = await axios.post(`${API}/reminders/monthly/send-now`);
      if (data.sent) setReminderMsg(`Aylık özet gönderildi · ${data.period} · ${data.stats?.policyCount || 0} poliçe`);
      else setReminderMsg(data.reason === "no-activity" ? "Önceki ay için üretim yok" : (data.reason === "already-sent" ? "Bu ay zaten gönderilmiş" : "Gönderilmedi"));
      await fetchAll();
    } catch (e) {
      setReminderMsg(`Gönderim başarısız: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setTestingReminder(false);
    }
  };
  return (
    <section className="panel cron-status-panel" data-testid="cron-status-panel" style={{ marginBottom: 20 }}>
      <div className="panel-head">
        <div>
          <h2>Otomatik hatırlatmalar</h2>
          <p>Sistem, aşağıdaki hatırlatmaları sizin adınıza otomatik gönderir. Program, son çalışma ve tercihleri buradan yönetin.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="text-button" onClick={fetchAll} data-testid="cron-status-refresh" title="Durumu yenile"><RefreshCw size={14} /> Yenile</button>
          <button className="outline-button" onClick={() => setEditingPrefs(v => !v)} data-testid="cron-edit-prefs-toggle"><SettingsIcon size={14} /> {editingPrefs ? "Tercihleri gizle" : "Tercihleri düzenle"}</button>
          <button className="outline-button" onClick={sendReminderNow} disabled={testingReminder} data-testid="cron-send-reminder-now"><Bell size={14} /> {testingReminder ? "Gönderiliyor…" : "Yenileme hatırlatması gönder"}</button>
        </div>
      </div>
      {reminderMsg && <div className="renewal-banner" data-testid="reminder-test-msg" style={{ marginBottom: 12 }}><Bell size={14} /><div><strong>{reminderMsg}</strong></div></div>}
      {prefsMsg && <div className="renewal-banner" data-testid="prefs-msg" style={{ marginBottom: 12 }}><Save size={14} /><div><strong>{prefsMsg}</strong></div></div>}

      {editingPrefs && (
        <div className="cron-prefs-grid" data-testid="cron-prefs-grid">
          <div className="cron-prefs-card">
            <div className="prefs-card-head"><FileText size={14} /> <strong>Haftalık yedek</strong>
              <label className="switch" title="Aç/kapat">
                <input type="checkbox" checked={prefs.backupEnabled} data-testid="pref-backup-enabled" disabled={savingPrefs}
                  onChange={(e) => savePrefs({ backupEnabled: e.target.checked })} />
                <span className="slider"></span>
              </label>
            </div>
            <div className="prefs-row">
              <label className="field"><span>Gün</span>
                <select data-testid="pref-backup-day" value={prefs.backupDay} disabled={!prefs.backupEnabled || savingPrefs}
                  onChange={(e) => savePrefs({ backupDay: Number(e.target.value) })}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </label>
              <label className="field"><span>Saat (UTC)</span>
                <select data-testid="pref-backup-hour" value={prefs.backupHour} disabled={!prefs.backupEnabled || savingPrefs}
                  onChange={(e) => savePrefs({ backupHour: Number(e.target.value) })}>
                  {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 (TR ${String(utcToTr(h)).padStart(2, "0")}:00)`}</option>)}
                </select>
              </label>
            </div>
            <div className="prefs-row prefs-sheets">
              <span className="prefs-sublabel">Yedek içeriği:</span>
              <div className="sheet-chips" data-testid="pref-backup-sheets">
                {[
                  { k: "policies", label: "Poliçeler" },
                  { k: "customers", label: "Müşteriler" },
                  { k: "renewals", label: "Yenilemeler" },
                  { k: "monthly", label: "Aylık Üretim" },
                ].map(({ k, label }) => (
                  <button key={k} type="button"
                    className={`bucket-chip ${prefs.backupSheets?.[k] ? "on" : "off"}`}
                    data-testid={`pref-sheet-${k}`}
                    onClick={() => {
                      const next = { ...(prefs.backupSheets || {}), [k]: !prefs.backupSheets?.[k] };
                      if (!Object.values(next).some(Boolean)) return;
                      savePrefs({ backupSheets: next });
                    }}
                    disabled={!prefs.backupEnabled || savingPrefs}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="prefs-row prefs-cc">
              <span className="prefs-sublabel">Ek alıcılar (CC) · max 3:</span>
              <BackupCcEditor
                emails={prefs.backupCc || []}
                disabled={!prefs.backupEnabled || savingPrefs}
                onSave={(list) => savePrefs({ backupCc: list })}
              />
            </div>
          </div>

          <div className="cron-prefs-card">
            <div className="prefs-card-head"><Bell size={14} /> <strong>Günlük yenileme hatırlatması</strong>
              <label className="switch" title="Aç/kapat">
                <input type="checkbox" checked={prefs.renewalEnabled} data-testid="pref-renewal-enabled" disabled={savingPrefs}
                  onChange={(e) => savePrefs({ renewalEnabled: e.target.checked })} />
                <span className="slider"></span>
              </label>
            </div>
            <div className="prefs-row">
              <label className="field"><span>Saat (UTC)</span>
                <select data-testid="pref-renewal-hour" value={prefs.renewalHour} disabled={!prefs.renewalEnabled || savingPrefs}
                  onChange={(e) => savePrefs({ renewalHour: Number(e.target.value) })}>
                  {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 (TR ${String(utcToTr(h)).padStart(2, "0")}:00)`}</option>)}
                </select>
              </label>
            </div>
            <div className="prefs-row prefs-buckets">
              <span className="prefs-sublabel">Hatırlatma eşikleri (gün):</span>
              <div className="bucket-chips" data-testid="pref-reminder-buckets">
                {AVAILABLE_BUCKETS.map((b) => (
                  <button key={b} type="button"
                    className={`bucket-chip ${prefs.reminderBuckets.includes(b) ? "on" : "off"}`}
                    data-testid={`pref-bucket-${b}`}
                    onClick={() => toggleBucket(b)}
                    disabled={!prefs.renewalEnabled || savingPrefs}>
                    {b}g
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="cron-prefs-card">
            <div className="prefs-card-head"><FileText size={14} /> <strong>Aylık portföy özeti</strong>
              <label className="switch" title="Aç/kapat">
                <input type="checkbox" checked={prefs.monthlySummaryEnabled} data-testid="pref-monthly-enabled" disabled={savingPrefs}
                  onChange={(e) => savePrefs({ monthlySummaryEnabled: e.target.checked })} />
                <span className="slider"></span>
              </label>
            </div>
            <div className="prefs-row">
              <label className="field"><span>Saat (UTC) · her ayın 1'i</span>
                <select data-testid="pref-monthly-hour" value={prefs.monthlySummaryHour} disabled={!prefs.monthlySummaryEnabled || savingPrefs}
                  onChange={(e) => savePrefs({ monthlySummaryHour: Number(e.target.value) })}>
                  {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 (TR ${String(utcToTr(h)).padStart(2, "0")}:00)`}</option>)}
                </select>
              </label>
            </div>
            <div className="prefs-row">
              <span className="prefs-sublabel">Her ayın 1'inde geçen ayın üretim özeti e-posta olarak gelir.</span>
            </div>
          </div>

          <div className="cron-prefs-card">
            <div className="prefs-card-head"><Sparkles size={14} /> <strong>Firma logosu</strong></div>
            <LogoUploader prefs={prefs} savingPrefs={savingPrefs} onChange={fetchAll} />
          </div>

          <div className="cron-prefs-card signature-card" data-testid="signature-card">
            <div className="prefs-card-head"><FileText size={14} /> <strong>E-posta imzası</strong></div>
            <SignatureEditor prefs={prefs} savingPrefs={savingPrefs} onSave={savePrefs} />
          </div>
        </div>
      )}

      <div className="cron-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        <div className={`cron-card tone-${w.enabled === false ? "warning" : toneFor(w.lastUserSent || w.lastGlobalRun, 10)}`} data-testid="cron-card-weekly">
          <div className="cron-card-head"><FileText size={14} /> <strong>Haftalık yedek</strong>{w.enabled === false && <span className="pref-off-pill">kapalı</span>}</div>
          <div className="cron-card-row"><span>Program:</span> <b>{w.scheduleHuman}</b></div>
          <div className="cron-card-row"><span>Size son gönderim:</span> <b data-testid="cron-weekly-user-last">{fmt(w.lastUserSent)}</b></div>
          <div className="cron-card-row"><span>Sistem son tetikleme:</span> <b data-testid="cron-weekly-last">{fmt(w.lastGlobalRun)}</b></div>
          <div className="cron-card-row"><span>Sıradaki çalışma:</span> <b data-testid="cron-weekly-next">{fmt(w.nextRun)}</b></div>
        </div>
        <div className={`cron-card tone-${r.enabled === false ? "warning" : toneFor(r.lastUserSent || r.lastGlobalRun, 3)}`} data-testid="cron-card-renewal">
          <div className="cron-card-head"><Bell size={14} /> <strong>Günlük yenileme hatırlatması</strong>{r.enabled === false && <span className="pref-off-pill">kapalı</span>}</div>
          <div className="cron-card-row"><span>Program:</span> <b>{r.scheduleHuman}</b></div>
          <div className="cron-card-row"><span>Eşikler:</span> <b data-testid="cron-renewal-buckets">{(r.buckets || []).map(b => `${b}g`).join(", ") || "—"}</b></div>
          <div className="cron-card-row"><span>Size son gönderim:</span> <b data-testid="cron-renewal-user-last">{fmt(r.lastUserSent)}</b></div>
          <div className="cron-card-row"><span>Sistem son tetikleme:</span> <b data-testid="cron-renewal-last">{fmt(r.lastGlobalRun)}</b></div>
          <div className="cron-card-row"><span>Sıradaki çalışma:</span> <b data-testid="cron-renewal-next">{fmt(r.nextRun)}</b></div>
        </div>
        <div className={`cron-card tone-${m.enabled === false ? "warning" : toneFor(m.lastUserSent, 35)}`} data-testid="cron-card-monthly">
          <div className="cron-card-head"><FileText size={14} /> <strong>Aylık portföy özeti</strong>{m.enabled === false && <span className="pref-off-pill">kapalı</span>}</div>
          <div className="cron-card-row"><span>Program:</span> <b>{m.scheduleHuman}</b></div>
          <div className="cron-card-row"><span>Size son gönderim:</span> <b data-testid="cron-monthly-user-last">{fmt(m.lastUserSent)}</b></div>
          <div className="cron-card-row"><span>Sıradaki çalışma:</span> <b data-testid="cron-monthly-next">{fmt(m.nextRun)}</b></div>
          <button className="text-button" onClick={sendMonthlyNow} disabled={testingReminder} data-testid="cron-send-monthly-now" style={{ marginTop: 6, alignSelf: "flex-start" }}>
            <RefreshCw size={12} /> Önceki ay özetini gönder
          </button>
        </div>
        <div className={`cron-card tone-${toneFor(t.lastGlobalRun, 3)}`} data-testid="cron-card-trash">
          <div className="cron-card-head"><Trash2 size={14} /> <strong>Çöp kutusu temizliği</strong></div>
          <div className="cron-card-row"><span>Program:</span> <b>{t.scheduleHuman}</b></div>
          <div className="cron-card-row"><span>Sistem son tetikleme:</span> <b data-testid="cron-trash-last">{fmt(t.lastGlobalRun)}</b></div>
          <div className="cron-card-row"><span>Sıradaki çalışma:</span> <b data-testid="cron-trash-next">{fmt(t.nextRun)}</b></div>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
        Not: Otomatik hatırlatmalar yalnızca <b>yayınlanmış (deploy edilmiş)</b> uygulamada çalışır. Saat UTC'dir; Türkiye saati parantez içinde gösterilir.
      </p>
      <EmailLogsPanel />
    </section>
  );
}
